import { runtimeEnv } from 'config/runtimeEnv';
import React, { createContext, useContext, useRef, useEffect, useState } from 'react';
import { initSocketConnection, subscribeToSocket } from 'utils/socket';
import { useDispatch, useSelector } from 'react-redux';
import { getServiceCity } from 'redux/actions/UserServiceCity';
import { isSuperAdmin, isAssumed } from 'utils/jwtRole';
import SOSAlertPopup from '../components/sos/SOSAlertPopup';

const SocketContext = createContext({
  socket: null,
  isConnected: false,
  drivers: [], // Add drivers to context
});

export function SocketProvider({ children }) {
  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [sosAlert, setSosAlert] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [drivers, setDrivers] = useState([]); // State for drivers data

  const locations = useSelector((state) => state?.userServiceCity?.userServiceCity?.data || []);
  const locationId = useSelector((state) => state?.locationDropdown?.selectedLocation?.id);

  // Fetch locations only on mount and when authenticated. Skip on the
  // platform seat (role 99, not borrowing): service-cities is tenant-scoped,
  // 403s for the platform token, and the response interceptor would clear the
  // session over it.
  useEffect(() => {
    const token = localStorage.getItem('token');
    const isAuth = token && token !== 'undefined' && token !== 'null';
    if (isSuperAdmin() && !isAssumed()) return;
    if (isAuth && (!locations || locations.length === 0)) {
      dispatch(getServiceCity());
    }
  }, []);

  // Main socket effect
  useEffect(() => {
    const token = localStorage.getItem('token');
    const isAuth = token && token !== 'undefined' && token !== 'null';
    if (!isAuth) return;
    // Platform seat has no tenant — livetrip rejects tenant-less handshakes.
    if (isSuperAdmin() && !isAssumed()) return;

    const url = runtimeEnv('B2B_API_BASE_URL');
    if (!url) return;

    console.log('Initializing socket for locationId:', locationId);

    // Initialize socket connection
    socketRef.current = initSocketConnection(url, {
      socketPath: '/livetrip-api/customer',
      locationId,
    });

    // Connection status handlers
    const onConnect = () => {
      setIsConnected(true);
      setDrivers([]);
      console.log('Socket connected');
    };

    const onDisconnect = () => {
      setIsConnected(false);
      setDrivers([]);
      console.log('Socket disconnected', locationId);
    };

    socketRef.current.on('connect', onConnect);
    socketRef.current.on('disconnect', onDisconnect);

    // SOS Alert handler
    const unsubscribeSos = subscribeToSocket('sosB2BAlert', (data) => {
      setSosAlert(data);
      setIsPopupOpen(true);
    });

    // Drivers location handlers
    const unsubscribeInitial = subscribeToSocket('InitialB2BLiveLocationFeed', (data) => {
      setDrivers(Array.isArray(data?.drivers) ? data.drivers : []);
    });

    // Cleanup function
    return () => {
      unsubscribeSos();
      unsubscribeInitial();
      if (socketRef.current) {
        socketRef.current.off('connect', onConnect);
        socketRef.current.off('disconnect', onDisconnect);
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
      setDrivers([]); // Clear drivers on disconnect
    };
  }, [locationId]);

  const handleTakeAction = () => {
    setIsPopupOpen(false);
    setSosAlert(null);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSosAlert(null);
  };

  // Stable context value
  const contextValue = {
    socket: socketRef.current,
    isConnected,
    drivers, // Include drivers in context
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
      <SOSAlertPopup
        sosAlert={sosAlert}
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        onTakeAction={handleTakeAction}
      />
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}
